/* SpecularButton — React Bits (JS-CSS variant), ported to a dependency-free custom element.
 * Source: https://reactbits.dev/r/SpecularButton-JS-CSS.json
 * Upstream renders the rim shader through `ogl`; this project has no bundler, so the
 * IDENTICAL GLSL (rounded-rect SDF + gaussian specular rim) runs on raw WebGL2, with the
 * same pointer-steering, proximity fade and angle smoothing. CSS is kept as published.
 *
 * <specular-button label="Buy" href="#buy" size="lg" radius="18" tint="#0A2E45"
 *   tint-opacity="1" text-color="#fff" line-color="#A5D64A" base-color="#0A2E45"
 *   intensity="1" shine-size="10" shine-fade="40" thickness="1" speed="0.35"
 *   follow-mouse proximity="250"></specular-button>
 */
(function () {
  'use strict';
  if (customElements.get('specular-button')) return;

  var PAD = 20;

  var CSS = `
specular-button { display:inline-flex; }
.specular-button { --sb-radius:18px; --sb-tint:#ffffff; --sb-tint-opacity:0; --sb-blur:0px; --sb-text-color:#f5f5f5;
  position:relative; display:inline-flex; align-items:center; justify-content:center; border:none; margin:0; font-family:inherit;
  font-weight:500; letter-spacing:0.01em; line-height:1; color:var(--sb-text-color);
  background:color-mix(in srgb, var(--sb-tint) calc(var(--sb-tint-opacity) * 100%), transparent);
  border-radius:var(--sb-radius); backdrop-filter:blur(var(--sb-blur)); -webkit-backdrop-filter:blur(var(--sb-blur));
  box-shadow:inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 24px rgba(0,0,0,0.25);
  cursor:pointer; outline:none; transition:transform .15s ease; }
.specular-button:active { transform:scale(0.97); }
.specular-button:focus-visible { outline:2px solid color-mix(in srgb, var(--sb-text-color) 60%, transparent); outline-offset:3px; }
.specular-button[disabled] { opacity:0.55; cursor:default; }
.specular-button[disabled]:active { transform:none; }
.specular-button--sm { font-size:0.85rem; padding:10px 22px; }
.specular-button--md { font-size:1rem; padding:14px 30px; }
.specular-button--lg { font-size:1.15rem; padding:18px 40px; }
.specular-button__fx { position:absolute; inset:-20px; pointer-events:none; z-index:1; }
.specular-button__fx canvas { display:block; width:100%; height:100%; }
.specular-button__label { position:relative; z-index:2; }`;

  if (!document.getElementById('specular-button-css')) {
    var st = document.createElement('style');
    st.id = 'specular-button-css';
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  var VERT = `#version 300 es
in vec2 position;
void main() { gl_Position = vec4(position, 0.0, 1.0); }`;

  var FRAG = `#version 300 es
precision highp float;

uniform vec2 uCenter;
uniform vec2 uHalfSize;
uniform float uRadius;
uniform float uAngle;
uniform float uPx;
uniform vec3 uLineColor;
uniform vec3 uBaseColor;
uniform float uIntensity;
uniform float uShineSize;
uniform float uShineFade;
uniform float uThickness;
uniform float uBaseWidth;

out vec4 fragColor;

float sdRoundedRect(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}

float shapeSDF(vec2 p) { return sdRoundedRect(p, uHalfSize, uRadius); }

float gaussianLine(float d, float sigma) {
  float x = d / (sigma + 1e-6);
  float k = mix(1.0, 1.6, smoothstep(0.0, 1.5, x));
  return exp(-k * x * x);
}

void main() {
  vec2 p = gl_FragCoord.xy - uCenter;
  float d = shapeSDF(p);
  vec2 L = vec2(cos(uAngle), sin(uAngle));

  float base = (1.0 - smoothstep(0.0, uBaseWidth, abs(d))) * 0.45;

  vec2 nEll = normalize(p / (uHalfSize * uHalfSize) + 1e-6);
  float phi = acos(clamp(abs(dot(nEll, L)), 0.0, 1.0));
  float rim = 1.0 - smoothstep(uShineSize - uShineFade, uShineSize + uShineFade + 1e-4, phi);
  float line = gaussianLine(d, uThickness);
  float edgeClamp = 1.0 - smoothstep(0.5 * uPx, 3.0 * uPx, abs(d));
  float hi = line * rim * edgeClamp * uIntensity;

  vec3 col = uBaseColor * base + uLineColor * hi;
  float a = clamp(base + hi, 0.0, 1.0);
  fragColor = vec4(col, a);
}`;

  function hexToRgb(hex) {
    var h = (hex || '#ffffff').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    /* the upstream Color class works in linear-ish sRGB floats */
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }
  var num = function (v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; };

  class SpecularButton extends HTMLElement {
    connectedCallback() {
      if (this._built) return;
      this._built = true;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'specular-button specular-button--' + (this.getAttribute('size') || 'lg');
      this.appendChild(btn);
      this.btn = btn;
      this.radius = num(this.getAttribute('radius'), 18);
      this.lineColor = this.getAttribute('line-color') || '#ffffff';
      this.baseColor = this.getAttribute('base-color') || '#525252';
      this.intensity = num(this.getAttribute('intensity'), 1);
      this.shineSize = num(this.getAttribute('shine-size'), 10);
      this.shineFade = num(this.getAttribute('shine-fade'), 40);
      this.thickness = num(this.getAttribute('thickness'), 1);
      this.speed = num(this.getAttribute('speed'), 0.35);
      this.followMouse = this.getAttribute('follow-mouse') !== 'false';
      this.proximity = num(this.getAttribute('proximity'), 250);
      this.autoAnimate = this.hasAttribute('auto-animate') && this.getAttribute('auto-animate') !== 'false';

      /* the inner button owns every style: React re-renders strip anything set
       * on the host element itself. */
      btn.style.setProperty('--sb-radius', this.radius + 'px');
      btn.style.setProperty('--sb-tint', this.getAttribute('tint') || '#ffffff');
      btn.style.setProperty('--sb-tint-opacity', this.getAttribute('tint-opacity') || '0');
      btn.style.setProperty('--sb-blur', num(this.getAttribute('blur'), 0) + 'px');
      btn.style.setProperty('--sb-text-color', this.getAttribute('text-color') || '#f5f5f5');

      var fx = document.createElement('span');
      fx.className = 'specular-button__fx';
      fx.setAttribute('aria-hidden', 'true');
      var label = document.createElement('span');
      label.className = 'specular-button__label';
      label.textContent = this.getAttribute('label') || '';
      btn.appendChild(fx);
      btn.appendChild(label);
      this._fx = fx;
      this._label = label;

      var self = this;
      var go = function () {
        var href = self.getAttribute('href');
        if (href) { if (href.charAt(0) === '#') { var t = document.querySelector(href); if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' }); } else { window.location.href = href; } }
      };
      btn.addEventListener('click', go);

      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) this._initGL();
    }

    attributeChangedCallback() {}

    disconnectedCallback() {
      cancelAnimationFrame(this._raf);
      if (this._ro) this._ro.disconnect();
      if (this._onMove) window.removeEventListener('pointermove', this._onMove);
      if (this._gl) { var ext = this._gl.getExtension('WEBGL_lose_context'); if (ext) ext.loseContext(); }
    }

    _initGL() {
      var canvas = document.createElement('canvas');
      var gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: true });
      if (!gl) return; /* graceful: the button still renders, just without the rim */
      this._gl = gl;
      this._fx.appendChild(canvas);

      var compile = function (type, src) {
        var s = gl.createShader(type);
        gl.shaderSource(s, src.trim());
        gl.compileShader(s);
        return s;
      };
      var prog = gl.createProgram();
      gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
      gl.bindAttribLocation(prog, 0, 'position');
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
      gl.useProgram(prog);

      /* fullscreen triangle, same geometry ogl's Triangle provides */
      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

      gl.clearColor(0, 0, 0, 0);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

      var u = {};
      ['uCenter','uHalfSize','uRadius','uAngle','uPx','uLineColor','uBaseColor','uIntensity','uShineSize','uShineFade','uThickness','uBaseWidth']
        .forEach(function (n) { u[n] = gl.getUniformLocation(prog, n); });

      var dpr = window.devicePixelRatio || 1;
      var self = this;
      var sizeRef = { w: 1, h: 1 };

      var resize = function () {
        var rect = self.btn.getBoundingClientRect();
        var w = rect.width, h = rect.height;
        if (!w || !h) return;
        sizeRef.w = w; sizeRef.h = h;
        canvas.width = Math.round((w + PAD * 2) * dpr);
        canvas.height = Math.round((h + PAD * 2) * dpr);
        canvas.style.width = (w + PAD * 2) + 'px';
        canvas.style.height = (h + PAD * 2) + 'px';
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform2f(u.uCenter, (PAD + w / 2) * dpr, (PAD + h / 2) * dpr);
        gl.uniform2f(u.uHalfSize, (w / 2) * dpr, (h / 2) * dpr);
      };
      this._ro = new ResizeObserver(resize);
      this._ro.observe(this.btn);
      resize();

      var pointerAngle = null, proximityT = 0;
      this._onMove = function (e) {
        var rect = self.btn.getBoundingClientRect();
        var cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
        var dx = Math.max(rect.left - e.clientX, 0, e.clientX - rect.right);
        var dy = Math.max(rect.top - e.clientY, 0, e.clientY - rect.bottom);
        var dist = Math.hypot(dx, dy);
        if (dist === 0) {
          var nx = (e.clientX - cx) / (rect.width / 2);
          var ny = (cy - e.clientY) / (rect.height / 2);
          pointerAngle = Math.atan2(2 / rect.height, -2 / rect.width) + nx * 0.3 + ny * 0.15;
        } else {
          pointerAngle = Math.atan2(cy - e.clientY, e.clientX - cx);
        }
        var t = Math.max(0, 1 - dist / Math.max(self.proximity, 1));
        proximityT = t * t * (3 - 2 * t);
      };
      window.addEventListener('pointermove', this._onMove);

      var angle = 2.4, idleAngle = 2.4, bright = 0, last = performance.now();
      var update = function (now) {
        self._raf = requestAnimationFrame(update);
        var dt = Math.min((now - last) / 1000, 0.05);
        last = now;

        idleAngle += self.speed * dt;
        var steer = self.followMouse && pointerAngle != null && (!self.autoAnimate || proximityT > 0);
        var target = steer ? pointerAngle : idleAngle;
        var diff = ((target - angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
        angle += diff * (1 - Math.exp(-dt * 7));

        var brightTarget = self.autoAnimate ? 1 : proximityT;
        bright += (brightTarget - bright) * (1 - Math.exp(-dt * 8));

        var lc = hexToRgb(self.lineColor), bc = hexToRgb(self.baseColor);
        gl.uniform1f(u.uAngle, angle);
        gl.uniform1f(u.uRadius, Math.min(self.radius, Math.min(sizeRef.w, sizeRef.h) / 2) * dpr);
        gl.uniform3f(u.uLineColor, lc[0], lc[1], lc[2]);
        gl.uniform3f(u.uBaseColor, bc[0], bc[1], bc[2]);
        gl.uniform1f(u.uIntensity, self.intensity * bright);
        gl.uniform1f(u.uShineSize, (self.shineSize * Math.PI) / 180);
        gl.uniform1f(u.uShineFade, (self.shineFade * Math.PI) / 180);
        gl.uniform1f(u.uThickness, self.thickness * dpr);
        gl.uniform1f(u.uPx, dpr);
        gl.uniform1f(u.uBaseWidth, dpr);

        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      };
      this._raf = requestAnimationFrame(update);
    }
  }

  customElements.define('specular-button', SpecularButton);
})();
